import React from 'react';

export default function PlatformSection() {
  return (
    <div className="py-24" id="platform">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="mb-6">
              <span className="bg-amiva-gradient-subtle text-amiva-primary text-sm font-medium px-4 py-2 rounded-full">
                AI-Native Platform
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Meet AMIVA+
              <br />
              <span className="text-amiva-primary">Your Sale Command Center</span>
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              The only platform that gives you complete visibility and control over your business
              sale. Watch in real-time as we connect with buyers, negotiate terms, and drive your
              sale forward.
            </p>

            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-amiva-primary rounded-lg flex items-center justify-center mr-4 mt-1">
                  <span className="text-white text-sm font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Real-Time Buyer Activity</h4>
                  <p className="text-gray-600">
                    See exactly which buyers are engaging, when they sign NDAs, and their level of
                    interest.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-8 h-8 bg-amiva-accent rounded-lg flex items-center justify-center mr-4 mt-1">
                  <span className="text-white text-sm font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Communication Logs</h4>
                  <p className="text-gray-600">
                    Every email, call, and interaction is logged and accessible to you instantly.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-8 h-8 bg-amiva-slate rounded-lg flex items-center justify-center mr-4 mt-1">
                  <span className="text-white text-sm font-bold">3</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">AI-Powered Insights</h4>
                  <p className="text-gray-600">
                    Get intelligent recommendations on pricing, timing, and buyer selection.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            {/* 3D Command Center Visualization */}
            <div className="relative perspective-1000">
              {/* Background glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-emerald-500/20 rounded-3xl blur-3xl"></div>

              {/* Main command center display */}
              <div className="relative bg-gray-900 rounded-2xl p-1 shadow-2xl transform rotate-y-5">
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 overflow-hidden">
                  {/* Header bar */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="flex space-x-1">
                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                        <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      </div>
                      <span className="text-gray-400 text-sm font-mono">AMIVA+ Command Center</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                      <span className="text-emerald-400 text-xs font-medium">LIVE</span>
                    </div>
                  </div>

                  {/* Main grid display */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {/* Buyer Activity Monitor */}
                    <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Buyer Activity
                        </h4>
                        <svg
                          className="w-4 h-4 text-emerald-400 animate-pulse"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0c0 .993-.241 1.929-.668 2.754l-1.524-1.525a3.997 3.997 0 00.078-2.183l1.562-1.562C15.802 8.249 16 9.1 16 10zm-5.165 3.913l1.58 1.58A5.98 5.98 0 0110 16a5.976 5.976 0 01-2.516-.552l1.562-1.562a4.006 4.006 0 001.789.027zm-4.677-2.796a4.002 4.002 0 01-.041-2.08l-.08.08-1.53-1.533A5.98 5.98 0 004 10c0 .954.223 1.856.619 2.657l1.54-1.54zm1.088-6.45A5.974 5.974 0 0110 4c.954 0 1.856.223 2.657.619l-1.54 1.54a4.002 4.002 0 00-2.346.033L7.246 4.668zM12 10a2 2 0 11-4 0 2 2 0 014 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-2 h-2 bg-emerald-400 rounded-full mr-2"></div>
                            <span className="text-gray-300 text-sm">PE Fund Alpha</span>
                          </div>
                          <span className="text-emerald-400 text-xs">Active</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
                            <span className="text-gray-300 text-sm">Strategic Corp</span>
                          </div>
                          <span className="text-blue-400 text-xs">Reviewing</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-2 h-2 bg-amber-400 rounded-full mr-2"></div>
                            <span className="text-gray-300 text-sm">Family Office</span>
                          </div>
                          <span className="text-amber-400 text-xs">NDA Sent</span>
                        </div>
                      </div>
                    </div>

                    {/* Deal Metrics */}
                    <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Deal Metrics
                        </h4>
                        <svg
                          className="w-4 h-4 text-blue-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                        </svg>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="text-2xl font-bold text-white">87%</div>
                          <div className="text-xs text-gray-400">Progress</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-emerald-400">12</div>
                          <div className="text-xs text-gray-400">NDAs</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-blue-400">3</div>
                          <div className="text-xs text-gray-400">LOIs</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-amber-400">5.2x</div>
                          <div className="text-xs text-gray-400">Multiple</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Activity Timeline */}
                  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                    <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">
                      Recent Activity
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center">
                            <svg
                              className="w-4 h-4 text-emerald-400"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-300">
                            New LOI received at $4.2M valuation
                          </p>
                          <p className="text-xs text-gray-500">2 hours ago</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                            <svg
                              className="w-4 h-4 text-blue-400"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                            </svg>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-300">
                            Management call scheduled with PE Fund
                          </p>
                          <p className="text-xs text-gray-500">5 hours ago</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Animated scan line */}
                  <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-emerald-400 to-transparent opacity-50 animate-scan"></div>
                  </div>
                </div>
              </div>

              {/* Floating elements for depth */}
              <div className="absolute -top-4 -right-4 bg-blue-500 text-white rounded-full p-3 shadow-xl animate-float">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  <path
                    fillRule="evenodd"
                    d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              <div
                className="absolute -bottom-4 -left-4 bg-emerald-500 text-white rounded-full p-3 shadow-xl animate-float"
                style={{ animationDelay: '1s' }}
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
